import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Parts from "@/pages/Parts";
import Purchases from "@/pages/Purchases";
import Orders from "@/pages/Orders";
import Billing from "@/pages/Billing";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Returns from "@/pages/Returns";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";
import Audit from "@/pages/Audit";
import SyncQueue from "@/pages/SyncQueue";
import Customers from "@/pages/Customers";
import Settings from "@/pages/Settings";
import { BranchProvider } from "@/context/BranchContext";
import "@/App.css";

const TOASTER_OPTIONS = { style: { borderRadius: 0, fontFamily: "IBM Plex Sans" } };

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <BranchProvider>
        <AppShell>{children}</AppShell>
      </BranchProvider>
    </ProtectedRoute>
  );
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={TOASTER_OPTIONS} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/" element={<Shell><Dashboard /></Shell>} />
          <Route path="/parts" element={<Shell><Parts /></Shell>} />
          <Route path="/purchases" element={<Shell><RoleGuard roles={["admin","inventory_manager","manager","accountant"]}><Purchases /></RoleGuard></Shell>} />
          <Route path="/orders" element={<Shell><RoleGuard roles={["admin","manager","retail_counter"]}><Orders /></RoleGuard></Shell>} />
          <Route path="/billing" element={<Shell><RoleGuard roles={["admin","manager","accountant","inventory_manager"]}><Billing /></RoleGuard></Shell>} />
          <Route path="/invoices" element={<Shell><Invoices /></Shell>} />
          <Route path="/invoices/:id" element={<Shell><InvoiceDetail /></Shell>} />
          <Route path="/returns" element={<Shell><RoleGuard roles={["admin","manager","accountant","retail_counter"]}><Returns /></RoleGuard></Shell>} />
          <Route path="/payments" element={<Shell><RoleGuard roles={["admin","accountant","manager"]}><Payments /></RoleGuard></Shell>} />
          <Route path="/reports" element={<Shell><RoleGuard roles={["admin","accountant","manager","inventory_manager"]}><Reports /></RoleGuard></Shell>} />
          <Route path="/users" element={<Shell><RoleGuard roles={["admin","manager"]}><Users /></RoleGuard></Shell>} />
          <Route path="/audit" element={<Shell><RoleGuard roles={["admin"]}><Audit /></RoleGuard></Shell>} />
          <Route path="/sync" element={<Shell><SyncQueue /></Shell>} />
          <Route path="/customers" element={<Shell><RoleGuard roles={["admin","manager","accountant","inventory_manager","retail_counter"]}><Customers /></RoleGuard></Shell>} />
          <Route path="/settings" element={<Shell><Settings /></Shell>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
